/**
 * Limites de compra por produto Woo a partir de MedicationDispense ativos.
 *
 * Fonte de verdade = quantity do dispense no servidor.
 * NÃO descontar pedidos Woo no client e NÃO PATCH de quantity pós-compra.
 * O backend controla saldo consumido vs. liberado.
 *
 * @module purchase-release/purchaseReleaseLimits
 */

import type { MedicationDispense } from "fhir/r5";
import dayjs from "dayjs";

import { WOO_PRODUCT_CODE_SYSTEM } from "./constants";
import {
  getDispenseProductName,
  getDispenseQuantity,
  getDispenseValidUntil,
  isActivePurchaseDispense,
} from "./listHelpers";

export interface ProductReleaseLimit {
  productId: number;
  quantity: number;
  validUntil?: string;
  productName?: string;
}

export function getDispenseWooProductId(
  dispense: MedicationDispense,
): number | null {
  const coding = dispense.medication?.concept?.coding?.find(
    (entry) => entry.system === WOO_PRODUCT_CODE_SYSTEM && entry.code,
  );
  if (!coding?.code) return null;
  const id = Number.parseInt(String(coding.code), 10);
  return Number.isFinite(id) ? id : null;
}

export function isDispenseReleaseCurrentlyValid(
  dispense: MedicationDispense,
  todayYmd = dayjs().format("YYYY-MM-DD"),
): boolean {
  if (!isActivePurchaseDispense(dispense)) return false;
  const validUntil = getDispenseValidUntil(dispense);
  if (!validUntil) return true;
  return !dayjs(validUntil).isBefore(dayjs(todayYmd), "day");
}

/** Soma quantidades liberadas por id Woo (somente dispenses ativos e dentro da validade). */
export function buildProductReleaseLimitsByWooId(
  dispenses: MedicationDispense[],
): Map<number, ProductReleaseLimit> {
  const map = new Map<number, ProductReleaseLimit>();
  const today = dayjs().format("YYYY-MM-DD");

  for (const dispense of dispenses) {
    if (!isDispenseReleaseCurrentlyValid(dispense, today)) continue;

    const productId = getDispenseWooProductId(dispense);
    if (productId === null) continue;

    const qty = getDispenseQuantity(dispense);
    if (qty <= 0) continue;

    const validUntil = getDispenseValidUntil(dispense);
    const productName = getDispenseProductName(dispense);
    const existing = map.get(productId);

    if (!existing) {
      map.set(productId, { productId, quantity: qty, validUntil, productName });
      continue;
    }

    existing.quantity += qty;
    if (validUntil && (!existing.validUntil || validUntil < existing.validUntil)) {
      existing.validUntil = validUntil;
    }
  }

  return map;
}
